// Pestanya "Resum" de Gestió d'un Empleat. Les hores són les de l'interval
// triat (horari del treballador i calendari de l'empresa). Les vacances
// pendents continuen sent les de l'any.

import React, { useState, useEffect, useCallback } from "react";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate } from "../../utils/Utilitats";
import { API_URL } from "../../utils/config";
import { formatMinuts } from "./ControlHorariEmpleat";
import "./resumempleat.css";

function isoLocal(data) {
  const any = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${any}-${mes}-${dia}`;
}

function formatDesviacio(minuts) {
  if (minuts > 0) return `+${formatMinuts(minuts)}`;
  return formatMinuts(minuts);
}

function LlistaDates({ dates, avui }) {
  if (!dates || dates.length === 0) return null;
  return (
    <ul className="resum-emp-llista-dates">
      {dates.map((d) => (
        <li key={d} className={d <= avui ? "resum-emp-passat" : "resum-emp-futur"}>{formatDate(d)}</li>
      ))}
    </ul>
  );
}

function ResumEmpleat({ idEmpleat, idEmpresa }) {
  const avuiDate = new Date();
  const [dataInici, setDataInici] = useState(isoLocal(new Date(avuiDate.getFullYear(), avuiDate.getMonth(), 1)));
  const [dataFinal, setDataFinal] = useState(isoLocal(avuiDate));
  const [dades, setDades] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [obert, setObert] = useState({});

  const carregar = useCallback(async () => {
    if (!idEmpleat || !idEmpresa) return;
    if (!dataInici || !dataFinal) return;
    if (dataInici > dataFinal) {
      setError("La data d'inici no pot ser posterior a la data final.");
      setDades(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        idEmpleat,
        idEmpresa,
        DataInici: dataInici,
        DataFinal: dataFinal,
      });
      const res = await fetchAutenticat(`${API_URL}/ControlHorariEmpleatComput.php?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setDades(data);
      } else {
        setError(data.message || "No s'ha pogut carregar el Resum.");
        setDades(null);
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setLoading(false);
    }
  }, [idEmpleat, idEmpresa, dataInici, dataFinal]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const alternar = (clau) => setObert((prev) => ({ ...prev, [clau]: !prev[clau] }));

  if (!idEmpleat || !idEmpresa) {
    return <p>Falten dades (Empleat o Empresa) per carregar el Resum.</p>;
  }

  const comput = dades?.comput;
  const vacances = dades?.vacances;
  const vacancesInterval = dades?.vacancesInterval;
  const diesEmpresa = dades?.diesEmpresa;
  const absencies = dades?.absencies;
  const baixes = dades?.baixes;
  const desviacio = comput ? (comput.horesTreballadesMinuts - comput.horesPrevistesMinuts) : 0;
  const pendent = comput?.horesPendentsMinuts ?? 0;
  const excedent = comput?.horesExcedentsMinuts ?? 0;

  return (
    <div className="resum-emp">
      <div className="resum-emp-nav">
        <label className="resum-emp-data">
          Des de
          <input type="date" value={dataInici} onChange={(e) => setDataInici(e.target.value)} />
        </label>
        <label className="resum-emp-data">
          Fins a
          <input type="date" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />
        </label>
      </div>

      {loading && <p>Carregant Resum...</p>}
      {error && <p className="resum-emp-error">{error}</p>}

      {!loading && !error && comput && (
        <>
          <div className="resum-emp-targeta">
            <div className="resum-emp-targeta-titol">
              Hores de l'interval ({formatDate(dataInici)} – {formatDate(dataFinal)})
            </div>
            <div className="resum-emp-xifres">
              <div className="resum-emp-xifra">
                <span className="resum-emp-xifra-valor">{formatMinuts(comput.horesObjectiuMinuts)}</span>
                <span className="resum-emp-xifra-etiqueta">Objectiu</span>
              </div>
              <div className="resum-emp-xifra">
                <span className="resum-emp-xifra-valor">{formatMinuts(comput.horesTreballadesMinuts)}</span>
                <span className="resum-emp-xifra-etiqueta">Treballades</span>
              </div>
              <div className="resum-emp-xifra">
                <span className="resum-emp-xifra-valor">{formatMinuts(comput.horesPrevistesMinuts)}</span>
                <span className="resum-emp-xifra-etiqueta">Previstes</span>
              </div>
              {excedent > 0 ? (
                <div className="resum-emp-xifra resum-emp-xifra-excedent">
                  <span className="resum-emp-xifra-valor">{formatMinuts(excedent)}</span>
                  <span className="resum-emp-xifra-etiqueta">Excedents</span>
                </div>
              ) : (
                <div className="resum-emp-xifra resum-emp-xifra-pendent">
                  <span className="resum-emp-xifra-valor">{formatMinuts(pendent)}</span>
                  <span className="resum-emp-xifra-etiqueta">Pendents</span>
                </div>
              )}
              <div className="resum-emp-xifra">
                <span className={`resum-emp-xifra-valor ${desviacio > 0 ? "resum-emp-desviacio-mes" : desviacio < 0 ? "resum-emp-desviacio-menys" : ""}`}>
                  {formatDesviacio(desviacio)}
                </span>
                <span className="resum-emp-xifra-etiqueta">Desviació</span>
              </div>
            </div>
            <p className="resum-emp-linia" style={{ marginTop: 10 }}>
              Les previstes són les hores del horari d'aquests dies, amb el calendari de l'empresa.
              La desviació és hores treballades menys previstes.
              Les pendents i els excedents es comparen amb l'objectiu.
              En un any sencer, l'objectiu és el mínim entre les previstes i les hores anuals del calendari;
              per això poden no coincidir amb la desviació.
            </p>
          </div>

          <div className="resum-emp-targeta">
            <div className="resum-emp-targeta-titol">Absències</div>
            <p className="resum-emp-linia">
              <strong>{absencies?.dies ?? 0}</strong> dies · <strong>{formatMinuts(absencies?.minuts ?? 0)}</strong>
            </p>
            {absencies?.dates?.length > 0 && (
              <>
                <button type="button" className="resum-emp-desplegable" onClick={() => alternar("absencies")}>
                  {obert.absencies ? "Amagar dates" : "Veure dates"}
                </button>
                {obert.absencies && <LlistaDates dates={absencies.dates} avui={dades.avui} />}
              </>
            )}
          </div>

          <div className="resum-emp-targeta">
            <div className="resum-emp-targeta-titol">Vacances</div>
            {vacances ? (
              <>
                <p className="resum-emp-linia">
                  {vacances.diesAnuals !== null ? `${vacances.diesAnuals} dies anuals ${vacances.any}` : `Any ${vacances.any}`}
                  {vacances.diesAnualsComplet !== null && vacances.diesAnualsComplet !== vacances.diesAnuals
                    ? ` (prorratejat, ${vacances.diesAnualsComplet} sencer)`
                    : ""}
                  {" · "}
                  <strong>{vacancesInterval?.gaudits ?? 0}</strong> gaudits a l'interval
                  {vacances.pendents !== null && <> · <strong>{vacances.pendents}</strong> pendents de l'any</>}
                </p>
                {vacancesInterval?.dates?.length > 0 && (
                  <>
                    <button type="button" className="resum-emp-desplegable" onClick={() => alternar("vacances")}>
                      {obert.vacances ? "Amagar dates" : "Veure dates de l'interval"}
                    </button>
                    {obert.vacances && <LlistaDates dates={vacancesInterval.dates} avui={dades.avui} />}
                  </>
                )}
              </>
            ) : (
              <p className="resum-emp-linia">Sense dades de Vacances.</p>
            )}
          </div>

          <div className="resum-emp-targeta">
            <div className="resum-emp-targeta-titol">Baixes</div>
            <p className="resum-emp-linia">
              <strong>{baixes?.dies ?? 0}</strong> dies a l'interval
            </p>
            {baixes?.dates?.length > 0 && (
              <>
                <button type="button" className="resum-emp-desplegable" onClick={() => alternar("baixes")}>
                  {obert.baixes ? "Amagar dates" : "Veure dates"}
                </button>
                {obert.baixes && <LlistaDates dates={baixes.dates} avui={dades.avui} />}
              </>
            )}
          </div>

          <div className="resum-emp-targeta">
            <div className="resum-emp-targeta-titol">Dies d'empresa</div>
            {diesEmpresa ? (
              <>
                <p className="resum-emp-linia">
                  <strong>{diesEmpresa.total}</strong> a l'interval · <strong>{diesEmpresa.gaudits}</strong> transcorreguts · <strong>{diesEmpresa.pendents}</strong> pendents
                </p>
                {diesEmpresa.dates.length > 0 && (
                  <>
                    <button type="button" className="resum-emp-desplegable" onClick={() => alternar("empresa")}>
                      {obert.empresa ? "Amagar dates" : "Veure dates"}
                    </button>
                    {obert.empresa && <LlistaDates dates={diesEmpresa.dates} avui={dades.avui} />}
                  </>
                )}
              </>
            ) : (
              <p className="resum-emp-linia">Sense dades de Dies d'Empresa.</p>
            )}
          </div>

          {dades.horesLliureDisposicio !== null && (
            <div className="resum-emp-nota">
              Lliure disposició {dades.any}: <strong>{dades.horesLliureDisposicio} h</strong>
              {dades.horesLliureDisposicioComplet !== null && dades.horesLliureDisposicioComplet !== dades.horesLliureDisposicio
                ? ` (prorratejat, ${dades.horesLliureDisposicioComplet} h sencer)`
                : ""}
              {" "}(informativa de l'any, no es descompta de l'interval).
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ResumEmpleat;
